/**
 * Influencer Banner
 * 
 * A small banner shown at the top of the app when a user came through
 * an influencer's branded landing page. Shows the influencer's branding
 * and allows the user to dismiss it.
 */

import React, { useState } from 'react';
import { useInfluencer } from '../contexts/InfluencerContext';
import { useSubscription } from '../hooks/useSubscription';
import { X } from 'lucide-react';

const InfluencerBanner: React.FC = () => {
  const { influencer, clearInfluencer } = useInfluencer();
  const { plan, isActive } = useSubscription();
  const [isDismissed, setIsDismissed] = useState(false);

  if (!influencer || isDismissed) {
    return null;
  }

  // Don't show banner if user already has a paid subscription
  if (plan && plan !== 'free' && isActive) {
    return null;
  }

  // Don't show banner if influencer offers 100% discount (user gets free premium access)
  // Users with 100% discount don't need to see the discount code banner
  if (influencer.specialOffer?.discountPercent === 100) {
    return null;
  }

  const primaryColor = influencer.branding?.primaryColor || '#3b82f6';

  return (
    <div 
      className="w-full py-2 px-4 flex items-center justify-between text-sm"
      style={{ 
        background: `linear-gradient(90deg, ${primaryColor}20, ${primaryColor}10)`,
        borderBottom: `1px solid ${primaryColor}30`,
      }}
    >
      <div className="flex items-center gap-3">
        {influencer.profileImage ? (
          <img 
            src={influencer.profileImage} 
            alt={influencer.name}
            className="w-6 h-6 rounded-full object-cover"
          />
        ) : (
          <div 
            className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {influencer.name.charAt(0)}
          </div>
        )}
        <span className="text-slate-300">
          Welcome from <span className="font-semibold text-white">{influencer.name}</span>
          {influencer.specialOffer?.discountCode && (
            <span className="ml-2">
              • Use code <span className="font-mono px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: `${primaryColor}30`, color: primaryColor }}>{influencer.specialOffer.discountCode}</span> for {influencer.specialOffer.discountPercent}% off
            </span>
          )}
        </span>
      </div>
      
      <button
        onClick={() => {
          setIsDismissed(true);
          clearInfluencer();
        }}
        className="text-slate-500 hover:text-slate-300 transition-colors p-1"
        title="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default InfluencerBanner;
